import { AccountClass, AccountCodeSchema, AccountIdSchema, AccountNameSchema, XeroAccountType, } from '@multiplier/lib-shared-account-types';
import { DateTimeSchema } from '@multiplier/lib-shared-date';
import { CurrencyCodeSchema, MonetaryLedgerBalanceBagSchema, MonetaryLedgerBalanceSchema, } from '@multiplier/lib-shared-monetary';
import { z } from 'zod';
// =============================================================================
// Schema factory: produces TrialBalanceRow and TrialBalance schemas for any value schema
// =============================================================================
const ExchangeRateJsonSchema = z.object({
    from: CurrencyCodeSchema,
    to: CurrencyCodeSchema,
    rate: z.string(),
});
/**
 * Creates TrialBalanceRow and TrialBalance schemas parameterized by value schema.
 *
 * Follows the same factory pattern as `createReportSchemas` in BusinessAnalysisSchemas.ts.
 */
function createTrialBalanceSchemas(valueSchema) {
    const trialBalanceRowSchema = z.object({
        accountId: AccountIdSchema.optional(),
        accountCode: AccountCodeSchema.optional(),
        accountName: AccountNameSchema,
        accountType: z.enum(XeroAccountType),
        accountClass: z.enum(AccountClass),
        monthDelta: valueSchema,
        yearToDateBalance: valueSchema,
        hasForeignActivity: z.boolean().optional(),
        annotation: z.string().optional(),
    });
    const trialBalanceSchema = z.object({
        reportDate: z.iso.date(),
        rows: z.array(trialBalanceRowSchema),
        exchangeRates: z.array(ExchangeRateJsonSchema).optional(),
    });
    return { trialBalanceRowSchema, trialBalanceSchema };
}
// =============================================================================
// MonetaryLedgerBalance instantiation (for API / single-currency)
// =============================================================================
const mlbSchemas = createTrialBalanceSchemas(MonetaryLedgerBalanceSchema);
export const TrialBalanceMlbRowSchema = mlbSchemas.trialBalanceRowSchema;
export const TrialBalanceMlbSchema = mlbSchemas.trialBalanceSchema;
// =============================================================================
// MoneyBag<LedgerBalance> instantiation (for CLI pipeline / multicurrency)
// =============================================================================
const moneyBagSchemas = createTrialBalanceSchemas(MonetaryLedgerBalanceBagSchema);
export const TrialBalanceMoneyBagRowSchema = moneyBagSchemas.trialBalanceRowSchema;
export const TrialBalanceMoneyBagSchema = moneyBagSchemas.trialBalanceSchema;
// =============================================================================
// Query schema for the trial balance API endpoint (discriminated union by source)
// =============================================================================
const trialBalanceBaseFields = {
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    date: z.iso.date().describe('Trial balance as-at date'),
};
export const TrialBalanceXeroQuerySchema = z.object({
    source: z.literal('xero'),
    ...trialBalanceBaseFields,
});
// TODO(PLA-1141): Extend with FX source configuration (e.g., fxSource discriminated union variant)
export const TrialBalanceBigQueryQuerySchema = z.object({
    source: z.literal('bigquery'),
    ...trialBalanceBaseFields,
    createdBefore: DateTimeSchema.optional().describe('ISO-8601 UTC timestamp. Only include journal entries posted on or before this time.'),
});
const trialBalanceQueryDiscriminatedUnion = z.discriminatedUnion('source', [
    TrialBalanceXeroQuerySchema,
    TrialBalanceBigQueryQuerySchema,
]);
// Backward compatibility: default to 'xero' when source is omitted by legacy clients
export const TrialBalanceQuerySchema = z.preprocess((val) => (typeof val === 'object' && val !== null && !('source' in val) ? { ...val, source: 'xero' } : val), trialBalanceQueryDiscriminatedUnion);
// =============================================================================
// Trial balance data source availability
// =============================================================================
export const TrialBalanceSourcesQuerySchema = z.object({
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
});
export const TrialBalanceSourcesResponseSchema = z.object({
    xero: z.boolean().describe('Whether Xero API is configured as a data source for this organization'),
    bigquery: z.boolean().describe('Whether BigQuery is configured as a data source for this organization'),
});
//# sourceMappingURL=TrialBalanceSchemas.js.map